import { useCallback, useEffect, useState } from 'react';
import { adminGetAuditEvents } from '../api/audit';

function formatMetadataPreview(value) {
  if (value === null || value === undefined) {
    return '';
  }
  try {
    const text = JSON.stringify(value);
    return text.length > 120 ? `${text.slice(0, 120)}...` : text;
  } catch (error) {
    return String(value);
  }
}

function AdminAuditPage() {
  const [action, setAction] = useState('');
  const [actorUserId, setActorUserId] = useState('');
  const [targetUserId, setTargetUserId] = useState('');
  const [limit, setLimit] = useState('200');
  const [events, setEvents] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState('');

  const loadEvents = useCallback(async () => {
    setErrorMessage('');
    setIsLoading(true);
    try {
      const data = await adminGetAuditEvents({
        action: action.trim() || undefined,
        actor_user_id: actorUserId.trim() || undefined,
        target_user_id: targetUserId.trim() || undefined,
        limit: limit.trim() || undefined,
      });
      setEvents(data);
    } catch (error) {
      setErrorMessage(error.message || 'Failed to load audit events.');
    } finally {
      setIsLoading(false);
    }
  }, [action, actorUserId, targetUserId, limit]);

  useEffect(() => {
    loadEvents();
  }, [loadEvents]);

  return (
    <div className="page page-shell page-stack">
      <section className="page-header">
        <div>
          <p className="eyebrow">Admin workspace</p>
          <h1>Admin: Audit log</h1>
          <p>Filter staff-side events and review recent onboarding or relationship-management activity.</p>
        </div>
      </section>
      <section className="card">
        <div className="section-header">
          <div>
            <p className="eyebrow">Query</p>
            <h2>Filters</h2>
          </div>
        </div>
        <div className="stack-sm">
          <div>
            <label htmlFor="audit-action">action</label>
            <input
              id="audit-action"
              type="text"
              value={action}
              onChange={(event) => setAction(event.target.value)}
            />
          </div>
          <div>
            <label htmlFor="audit-actor-user-id">actor_user_id</label>
            <input
              id="audit-actor-user-id"
              type="number"
              value={actorUserId}
              onChange={(event) => setActorUserId(event.target.value)}
            />
          </div>
          <div>
            <label htmlFor="audit-target-user-id">target_user_id</label>
            <input
              id="audit-target-user-id"
              type="number"
              value={targetUserId}
              onChange={(event) => setTargetUserId(event.target.value)}
            />
          </div>
          <div>
            <label htmlFor="audit-limit">limit</label>
            <input
              id="audit-limit"
              type="number"
              value={limit}
              onChange={(event) => setLimit(event.target.value)}
            />
          </div>
          <button type="button" className="btn btn-primary" onClick={loadEvents} disabled={isLoading}>
            {isLoading ? 'Loading...' : 'Refresh'}
          </button>
        </div>
      </section>
      <section className="card">
        <div className="section-header">
          <div>
            <p className="eyebrow">Results</p>
            <h2>Events</h2>
          </div>
        </div>
        {isLoading ? <p>Loading events...</p> : null}
        {errorMessage ? <p role="alert" className="notice notice-error">{errorMessage}</p> : null}
        {!isLoading && !errorMessage ? (
          <ul className="data-list">
            {events.map((event) => (
              <li key={`audit-${event.id}`} className="data-list-item">
                <p>
                  {event.created_at || '-'} | {event.action || '-'} | actor: {event.actor_user_id ?? '-'} | target:{' '}
                  {event.target_user_id ?? '-'}
                </p>
                <p className="muted-text">{formatMetadataPreview(event.metadata_json)}</p>
              </li>
            ))}
          </ul>
        ) : null}
        {!isLoading && !errorMessage && events.length === 0 ? (
          <p className="muted-text">No audit events found.</p>
        ) : null}
      </section>
    </div>
  );
}

export default AdminAuditPage;
